import { Badge } from "@/components/Badge";
import { Pill } from "@/components/StatPill";
import { Sun, Clock, Mail, Info } from "lucide-react";

export const SummerHeroSection = () => {
  return (
    <section className="hero-card">
      <div className="relative z-10">
        {/* Badge */}
        <div className="animate-fade-up" style={{ animationDelay: "100ms" }}>
          <Badge>Summer Email Performance Report</Badge>
        </div>
        
        {/* Headline */}
        <div className="mt-6 sm:mt-8 animate-fade-up" style={{ animationDelay: "200ms" }}>
          <div className="flex items-center gap-2 mb-2">
            <Sun className="w-5 h-5 text-warning" />
            <span className="text-sm font-medium text-warning">Seasonal Analysis</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight leading-[1.1] text-foreground">
            Why Summer Email
            <br />
            <span className="text-primary">Engagement Drops</span>
          </h1>
          <p className="font-display text-xl sm:text-2xl text-muted-foreground mt-3 font-medium">
            Vacation season, not deliverability failure
          </p>
        </div>
        
        {/* Subtitle */}
        <p className="text-muted-foreground text-base sm:text-lg leading-relaxed max-w-[700px] mt-5 animate-fade-up" style={{ animationDelay: "300ms" }}>
          From mid-June through late August, email reply rates and engagement fall across B2B 
          inboxes. Decision makers are on holiday, response times stretch out, and campaign 
          performance softens — <span className="font-semibold text-foreground">this is seasonal, not a technical problem</span>.
        </p>

        {/* Quick Stats Row */}
        <div className="flex flex-wrap gap-4 sm:gap-6 mt-8 animate-fade-up" style={{ animationDelay: "400ms" }}>
          <div className="flex items-center gap-2.5 text-sm sm:text-base text-muted-foreground bg-muted/50 rounded-full px-4 py-2">
            <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-warning" />
            <span><strong className="text-foreground">Mid-Jun → late Aug</strong> peak holiday window</span>
          </div>
          <div className="flex items-center gap-2.5 text-sm sm:text-base text-muted-foreground bg-muted/50 rounded-full px-4 py-2">
            <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
            <span><strong className="text-foreground">~3%</strong> OOO rate (Jun–Aug, ZoomInfo)</span>
          </div>
          <div className="flex items-center gap-2.5 text-sm sm:text-base text-muted-foreground bg-muted/50 rounded-full px-4 py-2">
            <Info className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
            <span><strong className="text-foreground">Seasonal</strong>, not deliverability</span>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="mt-10 animate-fade-up" style={{ animationDelay: "500ms" }}>
          <Pill accent="What's verifiable">
            ZoomInfo OOO data + HubSpot/Seguno qualitative seasonal coverage
          </Pill>

          <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mt-4 max-w-[820px]">
            Across the US and Europe, mid-June to late August is the peak holiday window for
            B2B buyers. Out-of-office reply rates rise modestly (ZoomInfo measured ~3% in
            Jun–Aug vs. ~2.7% annual median, with the real spike at Christmas week near 6.8%),
            response times stretch, and pipelines feel quieter. Inbox placement itself stays
            healthy — the slowdown is behavioural, not technical.
          </p>

          <div className="mt-5 rounded-xl border border-border bg-muted/40 p-4 sm:p-5 max-w-[820px]">
            <p className="text-xs sm:text-sm font-semibold text-foreground mb-1.5">
              A note on open rates
            </p>
            <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
              Open rates have been unreliable since Apple introduced Mail Privacy Protection in
              iOS 15 (2021), which pre-fetches images and inflates reported opens. For seasonal
              analysis we lead with <span className="font-medium text-foreground">reply rates,
              meeting bookings, and out-of-office trends</span> rather than open-rate precision.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
